/**
 * Amounts in Hebrew words, as written on a cheque:
 *   227065.9 → "מאתיים עשרים ושבעה אלף שישים וחמישה שקלים ותשעים אגורות"
 *
 * Shown in the synagogue-transaction confirmation so a misplaced decimal
 * point is obvious in plain language, not just in digits.
 *
 * Grammar: שקל is masculine (שלושה שקלים), אגורה is feminine (שלוש אגורות).
 * "ו" joins the last element of the number: אלף ומאתיים, מאה ועשרים,
 * מאתיים עשרים ושבעה אלף.
 */

import { toAgorot } from './money';

const UNITS_M = ['', 'אחד', 'שניים', 'שלושה', 'ארבעה', 'חמישה', 'שישה', 'שבעה', 'שמונה', 'תשעה'];
const UNITS_F = ['', 'אחת', 'שתיים', 'שלוש', 'ארבע', 'חמש', 'שש', 'שבע', 'שמונה', 'תשע'];
const TEENS_M = ['עשרה', 'אחד עשר', 'שנים עשר', 'שלושה עשר', 'ארבעה עשר', 'חמישה עשר', 'שישה עשר', 'שבעה עשר', 'שמונה עשר', 'תשעה עשר'];
const TEENS_F = ['עשר', 'אחת עשרה', 'שתים עשרה', 'שלוש עשרה', 'ארבע עשרה', 'חמש עשרה', 'שש עשרה', 'שבע עשרה', 'שמונה עשרה', 'תשע עשרה'];
const TENS = ['', '', 'עשרים', 'שלושים', 'ארבעים', 'חמישים', 'שישים', 'שבעים', 'שמונים', 'תשעים'];
const HUNDREDS = ['', 'מאה', 'מאתיים', 'שלוש מאות', 'ארבע מאות', 'חמש מאות', 'שש מאות', 'שבע מאות', 'שמונה מאות', 'תשע מאות'];
const THOUSANDS_1_TO_10 = ['', 'אלף', 'אלפיים', 'שלושת אלפים', 'ארבעת אלפים', 'חמשת אלפים', 'ששת אלפים', 'שבעת אלפים', 'שמונת אלפים', 'תשעת אלפים', 'עשרת אלפים'];

/** Largest whole-shekel amount we spell out (just under a billion). */
const MAX_SPELLED = 999_999_999;

/** Elements of 1..999, e.g. 227 → ["מאתיים", "עשרים", "שבעה"]. */
function elements(n: number, feminine: boolean): string[] {
  const out: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds) out.push(HUNDREDS[hundreds]);
  if (rest >= 10 && rest < 20) {
    out.push((feminine ? TEENS_F : TEENS_M)[rest - 10]);
  } else {
    const tens = Math.floor(rest / 10);
    const units = rest % 10;
    if (tens) out.push(TENS[tens]);
    if (units) out.push((feminine ? UNITS_F : UNITS_M)[units]);
  }
  return out;
}

/** "ו" before the last element of a multi-element group. */
function joinGroup(els: string[]): string[] {
  if (els.length < 2) return els;
  return [...els.slice(0, -1), 'ו' + els[els.length - 1]];
}

/** 1..999,999,999 in words. */
function numberWords(n: number, feminine: boolean): string {
  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor(n / 1000) % 1000;
  const units = n % 1000;

  const parts: string[][] = [];

  if (millions) {
    if (millions === 1) parts.push(['מיליון']);
    else if (millions === 2) parts.push(['שני מיליון']);
    else {
      const els = elements(millions, false);
      els[els.length - 1] += ' מיליון';
      parts.push(joinGroup(els));
    }
  }

  if (thousands) {
    if (thousands <= 10) parts.push([THOUSANDS_1_TO_10[thousands]]);
    else {
      const els = elements(thousands, false);
      els[els.length - 1] += ' אלף';
      parts.push(joinGroup(els));
    }
  }

  if (units) parts.push(joinGroup(elements(units, feminine)));

  // A single-element last part takes the "ו": אלף ומאתיים, מיליון ואלף.
  const last = parts[parts.length - 1];
  if (parts.length > 1 && last.length === 1) last[0] = 'ו' + last[0];

  return parts.flat().join(' ');
}

function shekelWords(n: number): string {
  if (n === 1) return 'שקל אחד';
  if (n === 2) return 'שני שקלים';
  return `${numberWords(n, false)} שקלים`;
}

function agorotWords(n: number): string {
  if (n === 1) return 'אגורה אחת';
  if (n === 2) return 'שתי אגורות';
  return `${numberWords(n, true)} אגורות`;
}

/**
 * Spell out a shekel amount (up to 2 decimals) in Hebrew.
 * Returns '' for amounts it can't spell (negative, non-finite, ≥ 1 billion)
 * — callers just hide the line in that case.
 */
export function amountToHebrewWords(amount: number): string {
  if (!Number.isFinite(amount) || amount < 0) return '';
  const totalAgorot = toAgorot(amount);
  const shekels = Math.floor(totalAgorot / 100);
  const agorot = totalAgorot % 100;
  if (shekels > MAX_SPELLED) return '';

  if (shekels === 0 && agorot === 0) return 'אפס שקלים';
  if (shekels === 0) return agorotWords(agorot);
  if (agorot === 0) return shekelWords(shekels);
  return `${shekelWords(shekels)} ו${agorotWords(agorot)}`;
}
